/**
 * MLB Player Profile Service
 *
 * Provides detailed MLB player profile data with stats for the TeamPlayerProfile component.
 * Extends mlbRosterService to provide full player profiles without API dependency.
 *
 * This solves the CORS issue with slam-gateway-4kyai23a.uc.gateway.dev by using local data.
 *
 * @example
 * import { getMLBPlayerProfile, getMLBPlayerStats } from './mlbPlayerProfileService';
 * const profile = getMLBPlayerProfile(543135);  // Gerrit Cole
 */

import { getMLBHeadshotUrl } from './mlbHeadshotService';
import { MLB_TEAMS } from './mlbRosterService';

// =============================================================================
// MLB PLAYER PROFILES (Extended data with career stats)
// =============================================================================

const MLB_PLAYER_PROFILES = {
  // Yankees
  543135: {
    mlbId: 543135,
    firstName: 'Gerrit',
    lastName: 'Cole',
    position: 'SP',
    number: 45,
    throws: 'R',
    bats: 'R',
    height: '6-4',
    weight: 220,
    birthDate: '1990-09-08',
    birthPlace: 'Newport Beach, CA',
    team: 'yankees',
    debutYear: 2013,
    college: 'UCLA',
    draftYear: 2011,
    draftRound: 1,
    draftPick: 1,
    bio: 'Gerrit Cole is an elite starting pitcher and the ace of the New York Yankees rotation. A former #1 overall pick, Cole has been one of baseball\'s most dominant pitchers, known for his powerful fastball and sharp slider. He won the 2023 AL Cy Young Award.',
    careerStats: {
      wins: 152,
      losses: 69,
      era: 3.17,
      games: 280,
      gamesStarted: 278,
      inningsPitched: 1756.1,
      strikeouts: 2033,
      walks: 418,
      whip: 1.02,
      kPer9: 10.4,
      avgAgainst: 0.213,
    },
    seasonStats: [
      { season: 2024, wins: 8, losses: 5, era: 3.41, games: 17, ip: 95.0, so: 99, bb: 22, whip: 1.12 },
      { season: 2023, wins: 15, losses: 4, era: 2.63, games: 33, ip: 209.0, so: 222, bb: 48, whip: 0.98 },
      { season: 2022, wins: 13, losses: 8, era: 3.50, games: 33, ip: 200.2, so: 257, bb: 41, whip: 1.02 },
    ],
    pitchArsenal: [
      { pitch: 'Four-Seam Fastball', avgVelo: 97.5, usage: 48.2, spinRate: 2518 },
      { pitch: 'Slider', avgVelo: 89.2, usage: 21.5, spinRate: 2814 },
      { pitch: 'Knuckle Curve', avgVelo: 84.1, usage: 15.3, spinRate: 3012 },
      { pitch: 'Changeup', avgVelo: 90.5, usage: 8.7, spinRate: 1742 },
      { pitch: 'Cutter', avgVelo: 92.8, usage: 6.3, spinRate: 2645 },
    ],
    awards: ['2023 AL Cy Young', '5x All-Star', '2019 ERA Title'],
  },
  664285: {
    mlbId: 664285,
    firstName: 'Carlos',
    lastName: 'Rodon',
    position: 'SP',
    number: 55,
    throws: 'L',
    bats: 'L',
    height: '6-3',
    weight: 245,
    birthDate: '1992-12-10',
    birthPlace: 'Miami, FL',
    team: 'yankees',
    debutYear: 2015,
    college: 'NC State',
    bio: 'Carlos Rodon is a power left-hander known for his electric fastball and devastating slider. After a breakout 2021 season with the White Sox, Rodon has established himself as one of the premier left-handed starters in baseball.',
    careerStats: {
      wins: 56,
      losses: 44,
      era: 3.42,
      games: 152,
      gamesStarted: 146,
      inningsPitched: 802.0,
      strikeouts: 955,
      walks: 268,
      whip: 1.15,
      kPer9: 10.7,
    },
    seasonStats: [
      { season: 2024, wins: 10, losses: 8, era: 4.08, games: 22, ip: 132.1, so: 158, bb: 44, whip: 1.21 },
      { season: 2023, wins: 3, losses: 8, era: 6.85, games: 12, ip: 64.1, so: 80, bb: 30, whip: 1.61 },
    ],
    pitchArsenal: [
      { pitch: 'Four-Seam Fastball', avgVelo: 94.8, usage: 52.1, spinRate: 2421 },
      { pitch: 'Slider', avgVelo: 85.3, usage: 38.5, spinRate: 2758 },
      { pitch: 'Changeup', avgVelo: 86.2, usage: 9.4, spinRate: 1654 },
    ],
  },
  592450: {
    mlbId: 592450,
    firstName: 'Aaron',
    lastName: 'Judge',
    position: 'RF',
    number: 99,
    throws: 'R',
    bats: 'R',
    height: '6-7',
    weight: 282,
    birthDate: '1992-04-26',
    birthPlace: 'Linden, CA',
    team: 'yankees',
    debutYear: 2016,
    college: 'Fresno State',
    bio: 'Aaron Judge is the Yankees captain and one of the most feared hitters in baseball. In 2022, he broke Roger Maris\'s American League single-season home run record with 62 homers. Standing 6\'7", Judge combines incredible power with a disciplined approach at the plate.',
    careerStats: {
      games: 769,
      atBats: 2729,
      runs: 571,
      hits: 789,
      homeRuns: 271,
      rbi: 582,
      stolenBases: 32,
      avg: 0.289,
      obp: 0.398,
      slg: 0.591,
      ops: 0.989,
      war: 36.7,
    },
    seasonStats: [
      { season: 2024, games: 95, avg: 0.318, hr: 35, rbi: 85, runs: 78, sb: 6, ops: 1.062 },
      { season: 2023, games: 106, avg: 0.267, hr: 37, rbi: 75, runs: 81, sb: 3, ops: 0.924 },
      { season: 2022, games: 157, avg: 0.311, hr: 62, rbi: 131, runs: 133, sb: 16, ops: 1.111 },
    ],
    battingMetrics: {
      exitVelocity: 95.8,
      launchAngle: 16.2,
      barrelRate: 22.5,
      hardHitRate: 54.8,
      chaseRate: 22.1,
      whiffRate: 28.4,
    },
    awards: ['2022 AL MVP', '4x All-Star', '2017 AL ROY', '2022 Silver Slugger'],
  },
  673962: {
    mlbId: 673962,
    firstName: 'Juan',
    lastName: 'Soto',
    position: 'LF',
    number: 22,
    throws: 'L',
    bats: 'L',
    height: '6-2',
    weight: 224,
    birthDate: '1998-10-25',
    birthPlace: 'Santo Domingo, DR',
    team: 'yankees',
    debutYear: 2018,
    bio: 'Juan Soto is one of baseball\'s best pure hitters, known for his exceptional plate discipline and power. At just 25 years old, Soto has already been an All-Star four times and won a World Series with the Nationals in 2019.',
    careerStats: {
      games: 785,
      atBats: 2617,
      runs: 508,
      hits: 774,
      homeRuns: 169,
      rbi: 467,
      stolenBases: 24,
      avg: 0.286,
      obp: 0.421,
      slg: 0.520,
      ops: 0.941,
      war: 30.2,
    },
    seasonStats: [
      { season: 2024, games: 98, avg: 0.298, hr: 27, rbi: 72, runs: 78, sb: 4, ops: 0.989 },
      { season: 2023, games: 162, avg: 0.275, hr: 35, rbi: 109, runs: 97, sb: 6, ops: 0.930 },
    ],
    battingMetrics: {
      exitVelocity: 92.5,
      launchAngle: 12.8,
      barrelRate: 15.2,
      hardHitRate: 48.1,
      chaseRate: 15.2,
      whiffRate: 18.8,
    },
    awards: ['4x All-Star', '2019 World Series Champion', '2020 Batting Title', '2x Silver Slugger'],
  },
  // Orioles
  680776: {
    mlbId: 680776,
    firstName: 'Gunnar',
    lastName: 'Henderson',
    position: 'SS',
    number: 2,
    throws: 'R',
    bats: 'L',
    height: '6-3',
    weight: 210,
    birthDate: '2001-06-29',
    birthPlace: 'Montgomery, AL',
    team: 'orioles',
    debutYear: 2022,
    bio: 'Gunnar Henderson is a rising star and the cornerstone of the Orioles\' rebuilt roster. The talented shortstop combines exceptional defensive skills with impressive power at the plate, quickly establishing himself as one of the best young players in baseball.',
    careerStats: {
      games: 246,
      atBats: 902,
      runs: 158,
      hits: 251,
      homeRuns: 48,
      rbi: 127,
      stolenBases: 23,
      avg: 0.278,
      obp: 0.362,
      slg: 0.518,
      ops: 0.880,
    },
    seasonStats: [
      { season: 2024, games: 98, avg: 0.282, hr: 28, rbi: 60, runs: 74, sb: 12, ops: 0.905 },
      { season: 2023, games: 140, avg: 0.255, hr: 28, rbi: 82, runs: 89, sb: 10, ops: 0.816 },
    ],
    battingMetrics: {
      exitVelocity: 90.8,
      launchAngle: 14.5,
      barrelRate: 12.8,
      hardHitRate: 45.2,
      chaseRate: 24.5,
      whiffRate: 25.8,
    },
    awards: ['2023 AL ROY Runner-up', '2024 All-Star'],
  },
  608665: {
    mlbId: 608665,
    firstName: 'Adley',
    lastName: 'Rutschman',
    position: 'C',
    number: 35,
    throws: 'R',
    bats: 'S',
    height: '6-2',
    weight: 220,
    birthDate: '1998-02-06',
    birthPlace: 'Portland, OR',
    team: 'orioles',
    debutYear: 2022,
    college: 'Oregon State',
    bio: 'Adley Rutschman, the #1 overall pick in 2019, has quickly established himself as the best catcher in baseball. A switch-hitter with exceptional plate discipline and elite defensive skills, Rutschman is the leader of the Orioles\' young core.',
    careerStats: {
      games: 286,
      atBats: 993,
      runs: 158,
      hits: 283,
      homeRuns: 41,
      rbi: 148,
      stolenBases: 1,
      avg: 0.285,
      obp: 0.382,
      slg: 0.474,
      ops: 0.856,
    },
    seasonStats: [
      { season: 2024, games: 98, avg: 0.265, hr: 16, rbi: 55, runs: 50, sb: 0, ops: 0.810 },
      { season: 2023, games: 135, avg: 0.280, hr: 20, rbi: 80, runs: 80, sb: 1, ops: 0.862 },
    ],
    catchingMetrics: {
      framingRuns: 12.5,
      popTime: 1.91,
      caughtStealing: 28,
      passedBalls: 4,
    },
    awards: ['2x All-Star', '2023 Gold Glove'],
  },
  // Tigers
  669373: {
    mlbId: 669373,
    firstName: 'Tarik',
    lastName: 'Skubal',
    position: 'SP',
    number: 29,
    throws: 'L',
    bats: 'L',
    height: '6-3',
    weight: 215,
    birthDate: '1996-11-20',
    birthPlace: 'Hayward, CA',
    team: 'tigers',
    debutYear: 2020,
    college: 'Seattle U',
    bio: 'Tarik Skubal emerged as one of baseball\'s best pitchers in 2024, winning the AL Cy Young Award. The left-hander combines a mid-90s fastball with an elite slider, establishing himself as the ace of the Tigers rotation.',
    careerStats: {
      wins: 38,
      losses: 28,
      era: 3.65,
      games: 88,
      gamesStarted: 86,
      inningsPitched: 478.0,
      strikeouts: 571,
      walks: 118,
      whip: 1.08,
      kPer9: 10.8,
    },
    seasonStats: [
      { season: 2024, wins: 18, losses: 4, era: 2.39, games: 31, ip: 192.0, so: 228, bb: 36, whip: 0.92 },
      { season: 2023, wins: 7, losses: 8, era: 4.51, games: 22, ip: 117.2, so: 134, bb: 32, whip: 1.20 },
    ],
    pitchArsenal: [
      { pitch: 'Four-Seam Fastball', avgVelo: 95.2, usage: 46.8, spinRate: 2385 },
      { pitch: 'Slider', avgVelo: 86.5, usage: 32.4, spinRate: 2612 },
      { pitch: 'Changeup', avgVelo: 87.8, usage: 14.5, spinRate: 1712 },
      { pitch: 'Sinker', avgVelo: 94.1, usage: 6.3, spinRate: 2156 },
    ],
    awards: ['2024 AL Cy Young', '2024 All-Star', '2024 ERA Title'],
  },
  682668: {
    mlbId: 682668,
    firstName: 'Riley',
    lastName: 'Greene',
    position: 'CF',
    number: 31,
    throws: 'L',
    bats: 'L',
    height: '6-3',
    weight: 200,
    birthDate: '2000-09-28',
    birthPlace: 'Orlando, FL',
    team: 'tigers',
    debutYear: 2022,
    bio: 'Riley Greene is the Tigers\' dynamic young center fielder with a well-rounded game. The left-handed hitter combines solid contact skills with developing power and excellent defensive range in the outfield.',
    careerStats: {
      games: 290,
      atBats: 1058,
      runs: 162,
      hits: 281,
      homeRuns: 38,
      rbi: 131,
      stolenBases: 23,
      avg: 0.266,
      obp: 0.348,
      slg: 0.439,
      ops: 0.787,
    },
    seasonStats: [
      { season: 2024, games: 98, avg: 0.272, hr: 16, rbi: 52, runs: 58, sb: 12, ops: 0.815 },
      { season: 2023, games: 124, avg: 0.256, hr: 16, rbi: 64, runs: 78, sb: 7, ops: 0.774 },
    ],
    battingMetrics: {
      exitVelocity: 89.5,
      launchAngle: 12.8,
      barrelRate: 9.8,
      hardHitRate: 38.5,
    },
    awards: ['2024 All-Star'],
  },
};

// =============================================================================
// PROFILE LOOKUP FUNCTIONS
// =============================================================================

/**
 * Get detailed player profile by MLB ID.
 * Falls back to basic roster data if full profile isn't available.
 *
 * @param {number|string} mlbId - MLB player ID
 * @returns {Object|null} - Player profile with stats
 */
export function getMLBPlayerProfile(mlbId) {
  const id = Number(mlbId);
  const profile = MLB_PLAYER_PROFILES[id];

  if (profile) {
    const team = MLB_TEAMS[profile.team];
    return {
      ...profile,
      name: `${profile.firstName} ${profile.lastName}`,
      teamName: team?.name || 'Unknown Team',
      teamAbbreviation: team?.abbreviation || '',
      teamColor: team?.primaryColor || '#003087',
      imageUrl: getMLBHeadshotUrl(id, { size: 'LARGE' }),
      image_url: getMLBHeadshotUrl(id, { size: 'MEDIUM' }),
      photo: getMLBHeadshotUrl(id, { size: 'LARGE' }),
    };
  }

  // If no detailed profile, return null (component should handle fallback)
  return null;
}

/**
 * Get player stats for TeamPlayerProfile component.
 * Returns data in the format expected by the component.
 *
 * @param {number|string} mlbId - MLB player ID
 * @returns {Object} - Stats data matching API response structure
 */
export function getMLBPlayerStats(mlbId) {
  const id = Number(mlbId);
  const profile = MLB_PLAYER_PROFILES[id];

  if (!profile) {
    return { data: [], total: 0, season_stats: [] };
  }

  const isPitcher = ['SP', 'RP', 'P'].includes(profile.position);

  // Format season stats to match API response structure
  const formattedStats = (profile.seasonStats || []).map(season => {
    if (isPitcher) {
      return {
        Season: season.season,
        Team: profile.team,
        GP: season.games,
        W: season.wins,
        L: season.losses,
        ERA: season.era,
        IP: season.ip,
        SO: season.so,
        BB: season.bb,
        WHIP: season.whip,
        // Additional fields for compatibility
        GamesPlayed: season.games,
        Wins: season.wins,
        Losses: season.losses,
        InningsPitched: season.ip,
        Strikeouts: season.so,
        Walks: season.bb,
      };
    } else {
      return {
        Season: season.season,
        Team: profile.team,
        GP: season.games,
        G: season.games,
        AB: Math.round(season.games * 3.8), // Approximate
        AVG: season.avg,
        HR: season.hr,
        RBI: season.rbi,
        R: season.runs,
        SB: season.sb || 0,
        OPS: season.ops,
        // Additional fields
        GamesPlayed: season.games,
        HomeRuns: season.hr,
        RunsBattedIn: season.rbi,
        Runs: season.runs,
        StolenBases: season.sb || 0,
      };
    }
  });

  return {
    data: formattedStats,
    total: formattedStats.length,
    season_stats: formattedStats,
  };
}

/**
 * Check if we have local profile data for a player.
 *
 * @param {number|string} mlbId - MLB player ID
 * @returns {boolean}
 */
export function hasMLBPlayerProfile(mlbId) {
  return Number(mlbId) in MLB_PLAYER_PROFILES;
}

/**
 * Get all available MLB player IDs with profiles.
 *
 * @returns {number[]}
 */
export function getAvailableMLBPlayerIds() {
  return Object.keys(MLB_PLAYER_PROFILES).map(Number);
}

/**
 * Process profile into format expected by TeamPlayerProfile component.
 *
 * @param {Object} profile - Raw profile data
 * @param {Array} stats - Stats array
 * @returns {Object} - Processed player object
 */
export function processMLBProfile(profile, stats) {
  if (!profile) return null;

  const isPitcher = ['SP', 'RP', 'P'].includes(profile.position);

  // Base player object - ensure all expected fields are present
  const fullName = profile.name || `${profile.firstName || ''} ${profile.lastName || ''}`.trim() || 'Unknown Player';
  const player = {
    id: profile.mlbId,
    player_id: profile.mlbId,
    mlbId: profile.mlbId,
    // Component uses both 'name' and 'fullName' - provide both
    name: fullName,
    fullName: fullName,
    Player: fullName,  // Some components use this
    firstName: profile.firstName || '',
    lastName: profile.lastName || '',
    position: profile.position || 'UTIL',
    jersey: profile.number?.toString() || '',
    height: profile.height || '',
    weight: profile.weight || '',
    team: profile.teamName || profile.team || '',
    team_name: profile.teamName || profile.team || '',
    teamAbbrev: profile.teamAbbreviation || '',
    imageUrl: profile.imageUrl || getMLBHeadshotUrl(profile.mlbId, { size: 'LARGE' }),
    image_url: profile.image_url || getMLBHeadshotUrl(profile.mlbId, { size: 'MEDIUM' }),
    photo: profile.photo || getMLBHeadshotUrl(profile.mlbId, { size: 'LARGE' }),
    bio: profile.bio || `MLB player who debuted in ${profile.debutYear || 'N/A'}.`,
    biography: profile.bio || '',
    birthDate: profile.birthDate || '',
    birthPlace: profile.birthPlace || '',
    college: profile.college || '',
    debutYear: profile.debutYear,
  };

  // Add position-specific stats
  if (isPitcher && profile.careerStats) {
    player.career_era = profile.careerStats.era;
    player.career_wins = profile.careerStats.wins;
    player.career_losses = profile.careerStats.losses;
    player.career_strikeouts = profile.careerStats.strikeouts;
    player.career_whip = profile.careerStats.whip;
    player.career_ip = profile.careerStats.inningsPitched;
    player.pitchArsenal = profile.pitchArsenal;
  } else if (profile.careerStats) {
    player.career_avg = profile.careerStats.avg;
    player.career_hr = profile.careerStats.homeRuns;
    player.career_rbi = profile.careerStats.rbi;
    player.career_ops = profile.careerStats.ops;
    player.career_sb = profile.careerStats.stolenBases;
    player.career_war = profile.careerStats.war;
    player.battingMetrics = profile.battingMetrics;
  }

  // Add awards
  player.awards = profile.awards || [];
  player.highlights = profile.awards?.map((award, i) => ({ id: i, text: award })) || [];

  return player;
}

// =============================================================================
// DYNAMIC PROFILE FETCHING (MLB Stats API Fallback)
// =============================================================================

/**
 * Fetch MLB player profile dynamically from MLB Stats API.
 * Uses local cache if available, otherwise fetches from API.
 *
 * @param {number|string} mlbId - MLB player ID
 * @returns {Promise<Object|null>} - Player profile data
 */
export async function fetchMLBPlayerProfile(mlbId) {
  const id = Number(mlbId);

  // Fast path: check local cache first
  const localProfile = getMLBPlayerProfile(id);
  if (localProfile) {
    console.log(`[MLBProfile] Using local profile for player ${id}`);
    return localProfile;
  }

  // Dynamic fetch from MLB Stats API
  console.log(`[MLBProfile] Fetching profile from MLB API for player ${id}`);

  try {
    const response = await fetch(
      `https://statsapi.mlb.com/api/v1/people/${id}`,
      { signal: AbortSignal.timeout(10000) }
    );

    if (!response.ok) {
      throw new Error(`MLB API returned ${response.status}`);
    }

    const data = await response.json();
    const person = data.people?.[0];

    if (!person) {
      console.warn(`[MLBProfile] No player found for ID ${id}`);
      return null;
    }

    // Transform MLB API format to our internal format
    const profile = {
      mlbId: person.id,
      firstName: person.firstName,
      lastName: person.lastName,
      position: person.primaryPosition?.abbreviation || 'UTIL',
      number: person.primaryNumber || '',
      throws: person.pitchHand?.code || '',
      bats: person.batSide?.code || '',
      height: person.height || '',
      weight: person.weight || '',
      birthDate: person.birthDate || '',
      birthPlace: person.birthCity ? `${person.birthCity}, ${person.birthStateProvince || person.birthCountry || ''}` : '',
      team: '', // Will be filled from roster context
      debutYear: person.mlbDebutDate ? new Date(person.mlbDebutDate).getFullYear() : null,
      draftYear: person.draftYear || null,
      active: person.active,
      bio: `${person.fullName} is a ${person.primaryPosition?.name || 'player'} who made their MLB debut in ${person.mlbDebutDate ? new Date(person.mlbDebutDate).getFullYear() : 'N/A'}.`,
    };

    // Add team name/color via roster lookup (will look up by current team if available)
    const team = person.currentTeam ? MLB_TEAMS[getTeamKeyByMlbId(person.currentTeam.id)] : null;
    if (team) {
      profile.team = team.teamId;
      profile.teamName = team.name;
      profile.teamAbbreviation = team.abbreviation;
      profile.teamColor = team.primaryColor;
    }

    // Add image URLs
    profile.imageUrl = getMLBHeadshotUrl(id, { size: 'LARGE' });
    profile.image_url = getMLBHeadshotUrl(id, { size: 'MEDIUM' });
    profile.photo = getMLBHeadshotUrl(id, { size: 'LARGE' });
    profile.name = `${profile.firstName} ${profile.lastName}`;

    console.log(`[MLBProfile] Fetched profile for ${person.fullName} (${id})`);
    return profile;
  } catch (error) {
    console.error(`[MLBProfile] MLB API fetch failed for ${id}:`, error.message);
    return null;
  }
}

/**
 * Fetch MLB player stats dynamically from MLB Stats API.
 *
 * @param {number|string} mlbId - MLB player ID
 * @param {string} position - Player position (to determine hitting/pitching)
 * @returns {Promise<Object>} - Stats data matching component format
 */
export async function fetchMLBPlayerStats(mlbId, position = '') {
  const id = Number(mlbId);

  // Fast path: check local cache first
  const localStats = getMLBPlayerStats(id);
  if (localStats && localStats.data && localStats.data.length > 0) {
    console.log(`[MLBStats] Using local stats for player ${id}`);
    return localStats;
  }

  // Determine if pitcher based on position
  const isPitcher = ['SP', 'RP', 'P', '1'].includes(position?.toUpperCase());
  const statGroup = isPitcher ? 'pitching' : 'hitting';

  console.log(`[MLBStats] Fetching ${statGroup} stats from MLB API for player ${id}`);

  try {
    const response = await fetch(
      `https://statsapi.mlb.com/api/v1/people/${id}/stats?stats=yearByYear&group=${statGroup}`,
      { signal: AbortSignal.timeout(10000) }
    );

    if (!response.ok) {
      throw new Error(`MLB API returned ${response.status}`);
    }

    const data = await response.json();
    const splits = data.stats?.[0]?.splits || [];

    // Filter to MLB regular season only and transform format
    const mlbSeasons = splits.filter(s =>
      s.sport?.abbreviation === 'MLB' && s.gameType === 'R'
    );

    const formattedStats = mlbSeasons.map(season => {
      const s = season.stat;
      if (isPitcher) {
        return {
          Season: season.season,
          Team: season.team?.name || '',
          TeamAbbrev: season.team?.abbreviation || '',
          GP: s.gamesPlayed || 0,
          GS: s.gamesStarted || 0,
          W: s.wins || 0,
          L: s.losses || 0,
          ERA: s.era || '0.00',
          IP: s.inningsPitched || '0.0',
          SO: s.strikeOuts || 0,
          BB: s.baseOnBalls || 0,
          WHIP: s.whip || '0.00',
          Wins: s.wins || 0,
          Losses: s.losses || 0,
          InningsPitched: s.inningsPitched || '0.0',
          Strikeouts: s.strikeOuts || 0,
          Walks: s.baseOnBalls || 0,
        };
      } else {
        return {
          Season: season.season,
          Team: season.team?.name || '',
          TeamAbbrev: season.team?.abbreviation || '',
          GP: s.gamesPlayed || 0,
          G: s.gamesPlayed || 0,
          AB: s.atBats || 0,
          AVG: s.avg || '.000',
          HR: s.homeRuns || 0,
          RBI: s.rbi || 0,
          R: s.runs || 0,
          SB: s.stolenBases || 0,
          OBP: s.obp || '.000',
          SLG: s.slg || '.000',
          OPS: s.ops || '.000',
          GamesPlayed: s.gamesPlayed || 0,
          HomeRuns: s.homeRuns || 0,
          RunsBattedIn: s.rbi || 0,
          Runs: s.runs || 0,
          StolenBases: s.stolenBases || 0,
          Hits: s.hits || 0,
        };
      }
    });

    // Sort by season descending (most recent first)
    formattedStats.sort((a, b) => Number(b.Season) - Number(a.Season));

    console.log(`[MLBStats] Fetched ${formattedStats.length} seasons for player ${id}`);

    return {
      data: formattedStats,
      total: formattedStats.length,
      season_stats: formattedStats,
    };
  } catch (error) {
    console.error(`[MLBStats] MLB API fetch failed for ${id}:`, error.message);
    return { data: [], total: 0, season_stats: [] };
  }
}

/**
 * Helper: Get team key by MLB team ID
 */
function getTeamKeyByMlbId(mlbTeamId) {
  for (const [key, team] of Object.entries(MLB_TEAMS)) {
    if (team.mlbTeamId === mlbTeamId) {
      return key;
    }
  }
  return null;
}

export default {
  getMLBPlayerProfile,
  getMLBPlayerStats,
  hasMLBPlayerProfile,
  getAvailableMLBPlayerIds,
  processMLBProfile,
  fetchMLBPlayerProfile,
  fetchMLBPlayerStats,
};
